import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../../core/platform/auth/AuthProvider";
import { inventoryApi, type Alerts } from "./api";
import { Ingredients } from "./Ingredients";
import { RecipeEditor } from "./RecipeEditor";

/**
 * The stock screens (T021, T034) — one overlay, three tabs.
 *
 * Styled after the menu manager, and for the same reason: it is used on the
 * till's own tablet, often mid-service, so every control clears the touch
 * minimum and nothing relies on an inner scrolling area — the menu manager
 * found those frozen on the tablet, so lists here are paged.
 */

type Tab = "stock" | "recipes" | "untracked";

export interface InventoryManagerProps {
  token: string;
  alerts: Alerts | null;
  onChanged(): void | Promise<void>;
  onClose(): void;
}

export function InventoryManager({ token, alerts, onChanged, onClose }: InventoryManagerProps) {
  const { can } = useAuth();
  const api = useMemo(() => inventoryApi(token), [token]);
  const canWrite = can("inventory.write");

  const [tab, setTab] = useState<Tab>("stock");
  const [recipeItemId, setRecipeItemId] = useState<string | null>(null);

  // The untracked list comes from the alerts the button already holds; make
  // sure it is current when someone looks at it.
  useEffect(() => {
    if (tab === "untracked") void onChanged();
  }, [tab, onChanged]);

  const untracked = alerts?.unmapped ?? [];

  return (
    <div className="inv" role="dialog" aria-label="Stock">
      <header className="inv__head">
        <h2 className="inv__title">Stock</h2>
        <button type="button" className="inv__close" onClick={onClose}>
          Done
        </button>
      </header>

      <nav className="inv__tabs" aria-label="Stock sections">
        <button
          type="button"
          className={`inv__tab${tab === "stock" ? " is-active" : ""}`}
          onClick={() => setTab("stock")}
        >
          Ingredients
        </button>
        {canWrite && (
          <button
            type="button"
            className={`inv__tab${tab === "recipes" ? " is-active" : ""}`}
            onClick={() => setTab("recipes")}
          >
            Recipes
          </button>
        )}
        <button
          type="button"
          className={`inv__tab${tab === "untracked" ? " is-active" : ""}`}
          onClick={() => setTab("untracked")}
        >
          Not tracked
          {untracked.length > 0 && <span className="inv__count">{untracked.length}</span>}
        </button>
      </nav>

      {tab === "stock" && <Ingredients api={api} token={token} onChanged={onChanged} />}

      {tab === "recipes" && canWrite && <RecipeEditor api={api} initialItemId={recipeItemId} />}

      {tab === "untracked" && (
        <section className="inv__panel">
          <p className="inv__hint">
            Sold with no recipe, so they moved no stock. Give one a recipe — even an empty one, for
            something like bottled water — and it leaves this list.
          </p>
          {untracked.length === 0 ? (
            <p className="inv__empty">Everything sold has a recipe.</p>
          ) : (
            <ul className="inv__list">
              {untracked.map((item) => (
                <li key={item.menuItemId} className="inv__row">
                  <span className="inv__row-name">{item.itemName}</span>
                  <span className="inv__row-figure">{item.quantitySold} sold</span>
                  {canWrite && (
                    <button
                      type="button"
                      className="inv__action"
                      onClick={() => {
                        setRecipeItemId(item.menuItemId);
                        setTab("recipes");
                      }}
                    >
                      Add recipe
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}
